<template>
	<v-card class="q-application-pdf" :class="mClass">
		<v-card-text class="q-application-pdf-preview">
			<div class="q-application-pdf-preview__actions">
				<v-btn fab small color="primary" :disabled="loading" @click="getDocument">
					<v-icon>icon-refresh-q</v-icon>
				</v-btn>
				<v-btn v-if="allowDownload" fab small color="primary" class="ml-2" :disabled="loading || !documentUrl" @click="downloadDocument">
					<v-icon>fas fa-download</v-icon>
				</v-btn>
			</div>

			<div v-if="loading" class="text-center px-4 py-12">
				<v-progress-linear indeterminate class="mt-6" />
				<br/>
				<span>Loading Document...</span>
			</div>

			<PdfPreview v-else-if="!isHiddenContent && documentUrl" :value="documentUrl" />

			<div v-else-if="isHiddenContent" class="text-center px-4 py-12">
				<span>You need to reload the Document.</span>
			</div>

			<div v-else-if="error" class="text-center px-4 py-12">
				<span>There was an error loading the document.</span>
			</div>
		</v-card-text>
	</v-card>
</template>

<script>
import QuilityAPI from '@/store/API/QuilityAPI.js'
import PdfPreview from '@/CMS/editor/components/PdfPreview.vue'

const defaultParams = {
	id: '',
	link: '',
}

export default {
    components: {
        PdfPreview,
    },
	props: {
		application: { type: Object, default: () => ({}) },
		secure: { type: [Boolean, Object], default: false },
	},
    data () {
        return {
            loading: false,
            error: false,
            currentDoc: null,
			params: { ...defaultParams },
			mediaQueryList: null,
			isHiddenContent: false,
			mountedInd: false,
        }
    },
	created () {
		this.getDocument()
	},
	mounted () {
		if (this.hasSecureListeners) {
			this.addListeners()
		}
		this.mountedInd = true
	},
	activated () {
		if (this.mountedInd) {
			this.mountedInd = false
			return
		}
		if (this.hasSecureListeners) {
			this.addListeners()
		}
	},
	deactivated () {
		if (this.hasSecureListeners) {
			this.removeListeners()
			this.reset()
		}
	},
	beforeDestroy () {
		this.removeListeners()
    },
	computed: {
		allowDownload () {
			if (typeof this.secure === 'boolean') { return !this.secure }
			return this.secure?.allowDownload || false
		},
		allowPrint () {
			if (typeof this.secure === 'boolean') { return !this.secure }
			return this.secure?.allowPrint || false
		},
		allowContextMenu () {
			if (typeof this.secure === 'boolean') { return !this.secure }
			return this.secure?.allowContextMenu || false
		},
		allowSelection () {
			if (typeof this.secure === 'boolean') { return !this.secure }
			return this.secure?.allowSelection || false
		},
		allowCopy () {
			if (typeof this.secure === 'boolean') { return !this.secure }
			return this.secure?.allowCopy || false
		},
		allowHiddenState () {
			if (typeof this.secure === 'boolean') { return !this.secure }
			return this.secure?.allowHiddenState || false
		},
		hasSecureListeners () {
			return !this.allowPrint ||
				!this.allowContextMenu ||
				!this.allowSelection ||
				!this.allowCopy ||
				!this.allowHiddenState
		},
		documentUrl () {
			if (!this.currentDoc) { return '' }
			return this.currentDoc
		},
		mClass () {
			return [
				!this.allowPrint && 'no-print',
				!this.allowSelection && 'no-select',
			].filter(Boolean)
		},
	},
	watch: {
		application: {
			deep: true,
			handler () {
				this.getDocument()
			},
		},
	},
    methods: {
        async getDocument () {
            this.loading = true
            this.error = false
            this.isHiddenContent = false
			this.currentDoc = null
			this.params = { ...defaultParams }
            const { id, link } = (this.application || {})
			const response = await QuilityAPI.getApplicationPdf(this.getLinkUrl(link))
			if (response.error) {
				this.error = true
				this.showError('Could not load Application PDF.<br>', response.msg)
			} else {
				if (this.loading && !this.isHiddenContent) {
					this.currentDoc = response
					this.params = { id, link }
				}
			}
			this.loading = false
        },
		getLinkUrl (link) {
			if (!link) { return undefined }
			const route = link.split('/api/')?.[1]
			return `/api/${route}`
		},
        downloadDocument () {
			if (!this.allowDownload) { return }
			if (!this.documentUrl) { return }
			const { id } = this.params
			const anchor = document.createElement('a')
			anchor.download = `Application-${id}.pdf`
			anchor.href = this.currentDoc
			anchor.click()
        },
		reset () {
			this.loading = false
			this.currentDoc = null
			this.params = { ...defaultParams }
			this.isHiddenContent = true
			this.$emit('hide')
		},
		onCopy (evt) {
			evt.clipboardData.setData('text/plain', 'No Copying!')
			evt.preventDefault()
			return false
		},
		onContextmenu (evt) {
			evt.preventDefault()
			return false
		},
		onSelection (evt) {
			window.getSelection().empty()
			evt.preventDefault()
			return false
		},
		onPrint (evt) {
			const keyCode = evt.keyCode ? evt.keyCode : evt.which
			const allowed = [9, 13, 27, 32]
			if (!allowed.includes(keyCode)) {
				navigator.clipboard.writeText('')
				evt.cancelBubble = true
				evt.preventDefault()
				evt.stopImmediatePropagation()
				return false
			}
		},
		onMatchMedia (evt) {
			if (evt.matches) {
				this.reset()
			}
		},
		onHiddenState () {
			if (document.hidden) {
				this.reset()
			}
		},
		addListeners () {
			if (!this.allowContextMenu) {
				document.addEventListener('contextmenu', this.onContextmenu)
			}
			if (!this.allowCopy) {
				document.addEventListener('copy', this.onCopy)
			}
			if (!this.allowSelection) {
				document.addEventListener('selectionchange', this.onSelection)
			}
			if (!this.allowPrint) {
				document.addEventListener('keydown', this.onPrint)
				if (window.matchMedia) {
					this.mediaQueryList = window.matchMedia('print')
					this.mediaQueryList.addEventListener('change', this.onMatchMedia)
				}
			}
			if (!this.allowHiddenState) {
				document.addEventListener('visibilitychange', this.onHiddenState)
			}
		},
		removeListeners () {
			document.removeEventListener('contextmenu', this.onContextmenu)
			document.removeEventListener('copy', this.onCopy)
			document.removeEventListener('selectionchange', this.onSelection)
			document.removeEventListener('keydown', this.onPrint)
			if (window.matchMedia) {
				this.mediaQueryList?.removeEventListener('change', this.onMatchMedia)
				this.mediaQueryList = null
			}
			document.removeEventListener('visibilitychange', this.onHiddenState)
		},
    },
}
</script>

<style lang="scss" scoped>
.q-application-pdf {
	.q-application-pdf-preview {
		position: relative;
		min-height: 11rem;
		padding: 0 !important;
	}
	.q-application-pdf-preview__actions {
		padding-right: 0.5rem;
		position: absolute;
		right: 0;
		z-index: 1;
	}
}

@media print {
	.no-print {
		display: none;
	}
}

.no-select {
	user-select: none;
	&::selection {
		background: none;
	}
}
</style>
